/* eslint-disable react/no-unused-prop-types */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/ban-types */
import { AxiosResponse } from 'axios';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { Grid, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutateFunction } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useMemo } from 'react';

import FormProvider from '@/commons/hook-form';
import { useSearchParams } from 'react-router-dom';
import { isOrderCustom } from '@/utils/validations';
import { translates } from '@/utils';
import { Order } from '../../types';
import { orderDetailSchema } from './validation';
import { ProductItemsListOrder } from './products-items-list';
import { defaultValuesProductForm } from './initial-values';
import { ProductItemsGridOrder } from './product-items-grid';
import { ProductItemsGridCustomOrder } from './product-items-custom';
import { Button } from '@PorkyCol/button';
import { cn } from '@PorkyCol/theme';

type FormValuesProps = Partial<any>;
type Props = {
  isForm?: boolean;
  isEdit?: boolean;
  currentProduct?: Order;
  isGrid?: boolean;
  defaultValues?: any;
  onSave?: UseMutateFunction<AxiosResponse<any, any>, unknown, void, unknown>;
  handleNext?: Function;
  handleSaved?: Function;
  activeStep?: number;
  size?: number;
  id?: string;
  isLoading: boolean;
};

export default function OrderDetailNewForm({
  isEdit,
  isGrid = false,
  currentProduct,
  defaultValues: defaultValuesItems,
  handleSaved,
  handleNext,
  id,
  onSave,
  isLoading,
}: Props) {
  const [searchParams] = useSearchParams();
  const isCustomOrder = isOrderCustom(searchParams.get('type') ?? '');
  const defaultValues = useMemo(
    () => defaultValuesProductForm(currentProduct),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentProduct]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(orderDetailSchema),
    defaultValues,
  } as any);

  const {
    reset,
    handleSubmit,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    if (currentProduct) {
      reset({ ...defaultValues } as unknown as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct]);

  useEffect(() => {
    if (isGrid) {
      reset({ ...defaultValues, items: [] } as unknown as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrid]);

  const onSubmit = async (values: FormValuesProps) => {
    try {
      const valuesFormatted = values.items.map((item: any) => ({
        description: item.description,
        productId: Number(item.product.value),
        quantity: Number(item.quantity),
        discount: {
          discount: Number(item.discount || 0),
          quantity_discount: Number(item.discountNumber || 0),
        },
      }));
      await onSave?.(valuesFormatted);
      handleSaved?.(valuesFormatted);
      handleNext?.();
      reset();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        <Grid item xs={12} md={12}>
          <Stack spacing={3}>
            {isCustomOrder ? (
              <ProductItemsGridCustomOrder
                id={id}
                isForm
                isEdit={isEdit}
                defaultValuesItems={defaultValuesItems}
              />
            ) : isGrid ? (
              <ProductItemsGridOrder isEdit={isEdit} />
            ) : (
              <ProductItemsListOrder
                id={id}
                isForm
                isEdit={isEdit}
                defaultValuesItems={defaultValuesItems}
              />
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={12}>
          <Button
            className={cn({
              '!bg-gray-800 border-gray-600 hover:border-gray-600': !isValid,
            })}
            disabled={!isValid}
            type="submit"
            variant="solid"
            color="success"
            size="md"
            loading={isLoading}
            fullWidth
          >
            {!isEdit ? [`${translates.create} ${translates.order}`] : [translates.saveChanges]}
          </Button>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
